import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Lock,
  Mail,
  User,
  CheckCircle2,
  AlertCircle,
  Hourglass,
  ShieldX,
} from "lucide-react";
import {
  fetchReaderPolicy,
  requestMagicLink,
  verifyMagicLink,
  setReaderCookie,
  type ReaderPolicy,
} from "../reader-auth";
import { Loading } from "./Loading";

/**
 * Pure domain check mirroring `readerAccessPolicy.emailMatchesAllowedDomains`
 * so we can prevalidate the input client-side and avoid a wasted request
 * when the policy already exposes the allow-list.
 */
function isEmailDomainAllowed(email: string, allowed: string[]): boolean {
  if (allowed.length === 0) return true;
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return allowed.some((d) => domain === d || domain.endsWith(`.${d}`));
}

type SignInState =
  | { kind: "idle" }
  | { kind: "sent"; email: string }
  | { kind: "pending_approval"; email: string }
  | { kind: "domain_not_allowed"; email: string; allowed: string[] }
  | { kind: "revoked"; email: string };

interface SignInProps {
  redirectTo?: string | null;
}

/**
 * `/sign-in` — collects email (+ optional name) and triggers the magic link.
 * After the user receives the email they land on `/sign-in/verify?token=…`,
 * which exchanges the token for a reader JWT and redirects into the space.
 */
export const SignIn: React.FC<SignInProps> = ({ redirectTo }) => {
  const [policy, setPolicy] = useState<ReaderPolicy | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<SignInState>({ kind: "idle" });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReaderPolicy()
      .then(setPolicy)
      .catch((e: Error) => setError(e.message));
  }, []);

  // Client-side prevalidation of the domain. The allow-list is already
  // public via the /policy endpoint, so there's no roster leakage: we
  // just catch the obvious case before round-tripping to the API.
  const trimmedEmail = email.trim().toLowerCase();
  const domainMismatch = useMemo(() => {
    if (!policy || !trimmedEmail.includes("@")) return false;
    return !isEmailDomainAllowed(trimmedEmail, policy.reader_allowed_domains);
  }, [policy, trimmedEmail]);

  if (!policy && !error) return <Loading />;

  if (policy && policy.visibility !== "logged_in") {
    return (
      <AuthShell policy={policy}>
        <div className="flex items-start gap-3 text-sm text-muted-foreground">
          <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <p>
            This space does not require sign-in.{" "}
            <a href="/" className="underline">
              Go home
            </a>
            .
          </p>
        </div>
      </AuthShell>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // Short-circuit when the policy already tells us the domain can't
    // sign in. Keeps the UX instant and avoids a useless POST.
    if (policy && domainMismatch) {
      setState({
        kind: "domain_not_allowed",
        email: trimmedEmail,
        allowed: policy.reader_allowed_domains,
      });
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await requestMagicLink({
        email: email.trim(),
        name: name.trim(),
        redirect_to: redirectTo ?? null,
      });
      if (res.status === "domain_not_allowed") {
        setState({
          kind: "domain_not_allowed",
          email: email.trim(),
          allowed: res.allowed_domains,
        });
      } else if (res.status === "pending_approval") {
        setState({ kind: "pending_approval", email: email.trim() });
      } else if (res.status === "revoked") {
        setState({ kind: "revoked", email: email.trim() });
      } else {
        // Treat "silent" identically to "sent" so we never reveal
        // whether the address is on the invite-only roster.
        setState({ kind: "sent", email: email.trim() });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // For `invite_only` spaces the roster already has the reader's name (the
  // admin supplied it when inviting), so we don't need to ask for it again.
  // For `open` spaces — the default — name is required so the roster always
  // has something human-friendly to display.
  const needsName = policy ? policy.reader_mode === "open" : false;
  const canSubmit =
    !!email.trim() &&
    (!needsName || name.trim().length > 0) &&
    !domainMismatch;

  if (!policy) {
    return (
      <AuthShell policy={null}>
        <div className="flex items-start gap-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <p>{error ?? "Unable to load sign-in."}</p>
        </div>
      </AuthShell>
    );
  }

  const resetToForm = () => {
    setState({ kind: "idle" });
    setError(null);
  };

  return (
    <AuthShell policy={policy}>
      {state.kind === "sent" ? (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-500" />
            <div>
              <p className="font-medium text-foreground">Check your email</p>
              <p className="text-muted-foreground">
                If <span className="font-medium">{state.email}</span> is
                allowed to read this space, we just sent a secure sign-in
                link. It expires in 15 minutes.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground self-start underline"
            onClick={resetToForm}
          >
            Use a different email
          </button>
        </div>
      ) : state.kind === "pending_approval" ? (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3">
            <Hourglass className="h-5 w-5 mt-0.5 text-amber-500" />
            <div>
              <p className="font-medium text-foreground">
                Awaiting admin approval
              </p>
              <p className="text-muted-foreground">
                We've received your request from{" "}
                <span className="font-medium">{state.email}</span>. Once an
                admin approves your access, you'll receive a sign-in link by
                email.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground self-start underline"
            onClick={resetToForm}
          >
            Use a different email
          </button>
        </div>
      ) : state.kind === "domain_not_allowed" ? (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3 text-destructive">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Email domain not allowed</p>
              <p className="text-muted-foreground mt-1">
                <span className="font-medium text-foreground">
                  {state.email}
                </span>{" "}
                can't sign in to this space. Only emails on these domains are
                allowed: {state.allowed.join(", ")}.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground self-start underline"
            onClick={resetToForm}
          >
            Use a different email
          </button>
        </div>
      ) : state.kind === "revoked" ? (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3 text-destructive">
            <ShieldX className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Access revoked</p>
              <p className="text-muted-foreground mt-1">
                Access for{" "}
                <span className="font-medium text-foreground">
                  {state.email}
                </span>{" "}
                has been revoked by an admin. Please contact the space owner
                if you believe this is a mistake.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground self-start underline"
            onClick={resetToForm}
          >
            Use a different email
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-foreground">Email</span>
            <div className="relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-background text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </label>

          {needsName && (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-foreground">Name</span>
              <div className="relative">
                <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-background text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </label>
          )}

          {policy.reader_allowed_domains.length > 0 && (
            <p
              className={
                domainMismatch
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {domainMismatch ? (
                <>
                  That email domain can't sign in. Allowed:{" "}
                  {policy.reader_allowed_domains.join(", ")}
                </>
              ) : (
                <>
                  Only emails on these domains can sign in:{" "}
                  {policy.reader_allowed_domains.join(", ")}
                </>
              )}
            </p>
          )}
          {policy.reader_mode === "invite_only" && (
            <p className="text-xs text-muted-foreground">
              Sign-in is invite-only. You&apos;ll only receive a link if your
              email is on the roster.
            </p>
          )}
          {policy.reader_requires_approval && (
            <p className="text-xs text-muted-foreground">
              New accounts require admin approval before they can view content.
            </p>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className="h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
};

interface VerifyProps {
  token: string;
}

export const SignInVerify: React.FC<VerifyProps> = ({ token }) => {
  const [status, setStatus] = useState<
    "loading" | "ok" | "pending_approval" | "revoked" | "error"
  >("loading");
  const [contextEmail, setContextEmail] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    verifyMagicLink(token)
      .then((res) => {
        if (cancelled) return;
        if (res.status === "pending_approval") {
          setContextEmail(res.email);
          setStatus("pending_approval");
          return;
        }
        if (res.status === "revoked") {
          setContextEmail(res.email);
          setStatus("revoked");
          return;
        }
        setReaderCookie(res.token);
        setStatus("ok");
        // Give the cookie a moment to persist before the redirect.
        const dest =
          res.redirect_to && res.redirect_to.startsWith("/")
            ? res.redirect_to
            : "/";
        setTimeout(() => window.location.replace(dest), 300);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setStatus("error");
        setMessage(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthShell policy={null}>
      {status === "loading" && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="h-4 w-4 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
          Verifying your sign-in link…
        </div>
      )}
      {status === "ok" && (
        <div className="flex items-center gap-3 text-sm text-foreground">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          Signed in. Redirecting…
        </div>
      )}
      {status === "pending_approval" && (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3">
            <Hourglass className="h-5 w-5 mt-0.5 text-amber-500" />
            <div>
              <p className="font-medium text-foreground">
                Thanks — we've sent your request to the admin
              </p>
              <p className="text-muted-foreground mt-1">
                {contextEmail ? (
                  <>
                    <span className="font-medium text-foreground">
                      {contextEmail}
                    </span>{" "}
                    is waiting for admin approval. Once approved you'll
                    receive a sign-in link by email — no need to request a
                    new one.
                  </>
                ) : (
                  <>
                    Your sign-in is waiting for admin approval. Once
                    approved you'll receive a sign-in link by email.
                  </>
                )}
              </p>
            </div>
          </div>
          <a
            href="/"
            className="text-xs text-muted-foreground hover:text-foreground self-start underline"
          >
            Go to homepage
          </a>
        </div>
      )}
      {status === "revoked" && (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3 text-destructive">
            <ShieldX className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Access revoked</p>
              <p className="text-muted-foreground mt-1">
                {contextEmail ? (
                  <>
                    Access for{" "}
                    <span className="font-medium text-foreground">
                      {contextEmail}
                    </span>{" "}
                    has been revoked by an admin. Please contact the space
                    owner if you believe this is a mistake.
                  </>
                ) : (
                  <>
                    Your access to this space has been revoked by an admin.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3 text-destructive">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Link invalid or expired</p>
              <p className="text-muted-foreground">
                {message ?? "Please request a new sign-in link."}
              </p>
            </div>
          </div>
          <a
            href="/sign-in"
            className="h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center hover:bg-primary/90"
          >
            Request a new link
          </a>
        </div>
      )}
    </AuthShell>
  );
};

interface AuthShellProps {
  policy: ReaderPolicy | null;
  children: React.ReactNode;
}

const AuthShell: React.FC<AuthShellProps> = ({ policy, children }) => {
  const heading =
    policy?.branding?.homepage_heading ||
    (policy ? policy.name : "Sign in to continue");
  const logoUrl = policy?.branding?.logo_url;
  const logoDarkUrl = policy?.branding?.logo_dark_url;
  const hasAnyLogo = Boolean(logoUrl || logoDarkUrl);
  // Mirror the logo swap used in `DocsSiteLayout` / `HomepageLandingView`:
  // render both images when available and let Tailwind's `dark:` variant
  // pick the right one. Falls back to the single image when only one side
  // is configured so we never end up showing nothing in one theme.
  const lightSrc = logoUrl || logoDarkUrl;
  const darkSrc = logoDarkUrl || logoUrl;
  const faviconUrl = policy?.branding?.favicon_url;

  return (
    <>
    {faviconUrl && (
      <Helmet>
        <link rel="icon" href={faviconUrl} sizes="any" type={faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png"} />
        <link rel="icon" href={faviconUrl} sizes="32x32" />
        <link rel="apple-touch-icon" sizes="180x180" href={faviconUrl} />
      </Helmet>
    )}
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-4 mb-8 text-center">
          {hasAnyLogo ? (
            <>
              {lightSrc && (
                <img
                  src={lightSrc}
                  alt={policy?.name ?? "Logo"}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain dark:hidden"
                />
              )}
              {darkSrc && (
                <img
                  src={darkSrc}
                  alt={policy?.name ?? "Logo"}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain hidden dark:block"
                />
              )}
            </>
          ) : (
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
              <Lock className="h-5 w-5 text-primary" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold text-foreground">{heading}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              This space is private. Sign in with your email to continue.
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {children}
        </div>
      </div>
    </div>
    </>
  );
};
